import {
  IconListItem,
  WSButton,
  WSButtons,
  WSContainer,
  WSElement,
  WSFlexBox,
  WSGridCentered,
  WSIcon,
  WSIconList,
  WSPanel,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { PreventLeave } from "../../../components/PreventLeave/PreventLeave";
import { ProjectOnboardingLayout } from "../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_COLLABORATORS } from "../../../query/payments/keys";
import {
  useCollaboratorsQuery,
  useListCollaboratorSettingsAdditionalData
} from "../../../query/payments/queries";
import { getVisibleCollaboratorsSortByCreatedAt } from "../../../query/payments/selectors";
import { paymentsService } from "../../../services/payments";
import { bulkActionFactory } from "../../../utils/bulkActionFactory";
import { getChangedData } from "../../../utils/getChangedData";
import { prepareCollaboratorLabelsRequest } from "../../../utils/jsonFormSchema";
import { pluralize } from "../../../utils/stringHelper";
import { getMatchingGroupedCollaborators } from "./bulkUploadCollaboratorsImporter";
import { UploadCollaborator } from "./BulkUploadCollaboratorsStep1";
import { defaultCollaboratorGroupOption } from "./BulkUploadCollaboratorsStep2";
import { getMatchingCollaborators } from "./collaboratorUtils";

interface UploadRequest {
  action: "create" | "update" | "addToGroup";
  data: UploadCollaborator;
}

interface UploadResult extends UploadRequest {
  error?: string;
}

export interface BulkUploadCollaboratorsStep2Props {}

const collaboratorPluralizeOptions = {
  one: "collaborator",
  other: "collaborators"
};

export const BulkUploadCollaboratorsStep3: React.FC<BulkUploadCollaboratorsStep2Props> = () => {
  useBrowserPageTitle("Wingspan - Bulk upload collaborators");
  const userId = useUserId();
  const history = useHistory<{
    results: UploadCollaborator[];
    selectedCollaboratorGroup: { label: string; value: string };
    nextUrl?: string;
  }>();
  const collaboratorsQuery = useCollaboratorsQuery();
  const existingCollaborators = getVisibleCollaboratorsSortByCreatedAt(
    collaboratorsQuery.data || []
  );
  const collaboratorSettingsAdditionalDataQuery = useListCollaboratorSettingsAdditionalData(
    {
      retry: false
    }
  );
  const additionalData = collaboratorSettingsAdditionalDataQuery.data || [];

  const initialResults = history.location.state?.results;
  const collaboratorGroup = history.location.state?.selectedCollaboratorGroup;
  const nextUrl = history.location.state?.nextUrl;

  const selectedCollaboratorGroup =
    collaboratorGroup.value === defaultCollaboratorGroupOption.value
      ? null
      : collaboratorGroup;
  const [uploadRequests, setUploadRequests] = useState<UploadRequest[]>([]);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const failedResults = uploadResults.filter(result => !!result.error);

  useEffect(() => {
    const _uploadRequests: UploadRequest[] = prepareUploadRequests();
    setUploadRequests(_uploadRequests);
  }, []);

  const prepareUploadRequests = () => {
    const _uploadRequests: UploadRequest[] = [];

    initialResults.forEach(collaborator => {
      const matchingCollaborators = getMatchingCollaborators(
        existingCollaborators,
        {
          email: collaborator.email,
          contractorId: collaborator.contractorId
        }
      );

      let matchingGroupedCollaborators = [];
      if (selectedCollaboratorGroup) {
        matchingGroupedCollaborators = getMatchingGroupedCollaborators(
          matchingCollaborators,
          selectedCollaboratorGroup.value
        );
      }

      if (selectedCollaboratorGroup) {
        if (matchingCollaborators.length > 0) {
          const unGroupedCollaborators = matchingCollaborators.filter(
            collaborator => !collaborator.collaboratorGroupId
          );
          if (unGroupedCollaborators.length > 0) {
            _uploadRequests.push({
              action: "addToGroup",
              data: collaborator
            });
          } else {
            _uploadRequests.push({
              action: "create",
              data: collaborator
            });
          }
        } else {
          _uploadRequests.push({
            action: "create",
            data: collaborator
          });
        }
      } else {
        // we are not supporting PATCH collaborator now except adding `collaboratorGroupId`
        if (matchingCollaborators.length === 0) {
          _uploadRequests.push({
            action: "create",
            data: collaborator
          });
        }
      }
    });
    return _uploadRequests;
  };

  const createCollaboratorLabels = (uploadRequest: UploadRequest) => {
    const labels = prepareCollaboratorLabelsRequest(
      additionalData,
      uploadRequest.data
    );

    if (uploadRequest.data.contractorId) {
      labels.contractorId = uploadRequest.data.contractorId;
    }

    return labels;
  };

  const bulkUpload = bulkActionFactory({
    action: async (uploadRequest: UploadRequest) => {
      try {
        if (uploadRequest.action === "create") {
          // Create
          await paymentsService.collaborator.create({
            clientId: userId,
            memberEmail: uploadRequest.data.email,
            memberName: `${uploadRequest.data.firstName} ${uploadRequest.data.lastName}`,
            labels: createCollaboratorLabels(uploadRequest),
            ...(selectedCollaboratorGroup
              ? { collaboratorGroupId: selectedCollaboratorGroup?.value }
              : {})
          });
        } else if (uploadRequest.action === "addToGroup") {
          const matchingCollaborators = getMatchingCollaborators(
            existingCollaborators,
            {
              email: uploadRequest.data.email,
              contractorId: uploadRequest.data.contractorId
            }
          );
          if (matchingCollaborators.length > 0) {
            const unGroupedCollaborators = matchingCollaborators.filter(
              collaborator => !collaborator.collaboratorGroupId
            );
            const matchingCollaborator = unGroupedCollaborators[0];
            await paymentsService.collaborator.update(
              matchingCollaborator.collaboratorId,
              {
                collaboratorGroupId: selectedCollaboratorGroup?.value
              }
            );
          }
        } else {
          // Update
          const matchingCollaborators = getMatchingCollaborators(
            existingCollaborators,
            {
              email: uploadRequest.data.email,
              contractorId: uploadRequest.data.contractorId
            }
          );

          if (matchingCollaborators.length > 0) {
            const matchingCollaborator = matchingCollaborators[0];
            await paymentsService.collaborator.update(
              matchingCollaborator.collaboratorId,
              getChangedData(matchingCollaborator, {
                labels: createCollaboratorLabels(uploadRequest)
              })
            );
          } else {
            throw new Error("Internal error");
          }
        }

        setUploadResults(prevValue => [...prevValue, uploadRequest]);
      } catch (error) {
        setUploadResults(prevValue => [
          ...prevValue,
          {
            ...uploadRequest,
            error: (error as any).response?.data.error || "Oops!"
          }
        ]);
      }
    }
  });

  const [confirmAndImport, confirmAndImportMeta] = useWSMutation(
    async () => {
      await bulkUpload.run(uploadRequests);
    },
    {
      dependencies: [QUERY_COLLABORATORS]
    }
  );

  const [retry, retryMeta] = useWSMutation(
    async () => {
      const newUploadRequests = failedResults.map(result => ({
        action: result.action,
        data: result.data
      }));
      setUploadRequests(newUploadRequests);
      setUploadResults([]);
      await bulkUpload.run(newUploadRequests);
    },
    {
      dependencies: [QUERY_COLLABORATORS]
    }
  );

  const importPercent = (uploadResults.length * 100) / uploadRequests.length;

  const uploadCreateRequests = uploadRequests.filter(
    request => request.action === "create"
  );
  const uploadUpdateRequests = uploadRequests.filter(
    request => request.action === "update"
  );
  const uploadAddToGroupRequests = uploadRequests.filter(
    request => request.action === "addToGroup"
  );

  const failedCsvProps = {
    filename: `Wingspan_Failed_Bulk_Upload_Collaborators_${Date.now()}.csv`,
    headers: [
      { label: "First Name", key: "firstName" },
      { label: "Last Name", key: "lastName" },
      { label: "Email", key: "email" },
      { label: "Phone number", key: "phoneNumber" },
      { label: "External Collaborator ID", key: "contractorId" },
      ...additionalData.map(field => ({ label: field.name, key: field.key })),
      { label: "Error", key: "error" }
    ],
    data: failedResults.map(result => ({ ...result.data, error: result.error }))
  };
  const isUploading = confirmAndImportMeta.isLoading || retryMeta.isLoading;

  return (
    <ProjectOnboardingLayout progress={(100 / 3) * 3}>
      <WSContainer verticalPadding>
        <WSGridCentered M={8} S={6} XS={6}>
          <WSText.Label color="grey600" mt="S" mb="M" pt="S">
            3 of 3
          </WSText.Label>
          <WSText.Heading2>
            Bulk upload collaborators with a spreadsheet
          </WSText.Heading2>
          <WSPanel mt="L">
            {isUploading || uploadResults.length > 0 ? (
              <>
                <WSText.Heading3 mb="M">
                  Your information upload summary
                </WSText.Heading3>

                <WSElement mb="L">
                  {failedResults.length > 0 ? (
                    <WSFlexBox.CenterY>
                      <WSIcon name="warning" color="coral" mr="S" />
                      <WSText>
                        {failedResults.length}/{uploadRequests.length}{" "}
                        {pluralize(
                          failedResults.length,
                          collaboratorPluralizeOptions
                        )}{" "}
                        failed to upload
                      </WSText>
                    </WSFlexBox.CenterY>
                  ) : (
                    <WSText>
                      {uploadResults.length}{" "}
                      {pluralize(
                        uploadResults.length,
                        collaboratorPluralizeOptions
                      )}{" "}
                      successfully uploaded.
                    </WSText>
                  )}
                </WSElement>

                {failedResults.length > 0 ? (
                  <CSVLink {...failedCsvProps}>
                    <WSButton.Link mb="S">
                      Download failed uploads
                    </WSButton.Link>
                  </CSVLink>
                ) : null}

                {isUploading ? (
                  <>
                    <PreventLeave
                      isEnabled={isUploading}
                      promptMessage="Your collaborators have already been uploaded and are in the process of being imported. Cancelling now can cause data issues. Are you sure you want to leave the page?"
                    />
                    <WSProgressBar
                      mt="L"
                      percent={importPercent}
                      text={`Uploading: ${uploadResults.length} of ${
                        uploadRequests.length
                      } ${pluralize(
                        uploadResults.length,
                        collaboratorPluralizeOptions
                      )}...`}
                    />
                  </>
                ) : null}
                {failedResults.length > 0 ? (
                  <WSButtons forceFullWidth>
                    <WSButton fullWidth onClick={retry}>
                      Try again
                    </WSButton>
                    <WSButton.Secondary
                      fullWidth
                      onClick={() =>
                        history.push("/member/invoices/contacts/collaborators")
                      }
                    >
                      Go to collaborators
                    </WSButton.Secondary>
                  </WSButtons>
                ) : nextUrl ? (
                  <WSButton
                    disabled={isUploading}
                    fullWidth
                    onClick={() =>
                      history.push(
                        nextUrl ?? "/member/invoices/contacts/collaborators"
                      )
                    }
                  >
                    Continue to 1099-NEC Filing
                  </WSButton>
                ) : (
                  <WSButtons forceFullWidth>
                    <WSButton
                      onClick={() =>
                        history.push("/member/invoices/contacts/collaborators")
                      }
                    >
                      Go to collaborators
                    </WSButton>
                    <WSButton.Secondary
                      onClick={() => {
                        history.push(
                          "/member/invoices/contacts/collaborators/bulk-create/info"
                        );
                      }}
                    >
                      Restart upload collaborators
                    </WSButton.Secondary>
                  </WSButtons>
                )}
              </>
            ) : (
              <>
                <WSText.Heading3 mb="M">Import Preview</WSText.Heading3>
                <WSIconList
                  mb="L"
                  items={[
                    {
                      description: `${uploadRequests.length} ${pluralize(
                        uploadRequests.length,
                        collaboratorPluralizeOptions
                      )} will be imported.`,
                      icon: {
                        name: "personalInfo",
                        color: "blue500",
                        size: "M"
                      }
                    },
                    {
                      description: `${
                        uploadCreateRequests.length
                      } new ${pluralize(
                        uploadCreateRequests.length,
                        collaboratorPluralizeOptions
                      )} will be ${
                        selectedCollaboratorGroup
                          ? `created and added to ${selectedCollaboratorGroup.label}`
                          : "created."
                      } `,
                      icon: {
                        name: "circlePlus",
                        color: "blue500",
                        size: "M"
                      }
                    },
                    ...(selectedCollaboratorGroup
                      ? [
                          {
                            description: `${
                              uploadAddToGroupRequests.length
                            } existing ${pluralize(
                              uploadAddToGroupRequests.length,
                              collaboratorPluralizeOptions
                            )} will be added to ${
                              selectedCollaboratorGroup?.label
                            }.`,
                            icon: {
                              name: "pencil",
                              color: "blue500",
                              size: "M"
                            }
                          } as IconListItem
                        ]
                      : [])
                  ]}
                />

                <WSButton
                  icon="upload"
                  fullWidth
                  onClick={confirmAndImport}
                  loading={confirmAndImportMeta.isLoading}
                >
                  Import Collaborators
                </WSButton>
              </>
            )}
          </WSPanel>
        </WSGridCentered>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
